import { useEffect, useState } from "react";
import { Eye, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { LoadingState, ErrorState } from "@/components/shared/States";

const TONE = { INTERNAL: "border-l-jigsaw-navy", CLIENT: "border-l-jigsaw-orange", SUPPLIER: "border-l-jigsaw-success" };

export function PersonasTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState("JIGSAW_ADMIN");
  useEffect(() => { api.get("/admin/personas").then((r) => setData(r.data)).catch(() => setError(true)); }, []);
  if (error) return <ErrorState testId="admin-personas-error" />;
  if (!data) return <LoadingState testId="admin-personas-loading" />;
  const sel = data.roles.find((r) => r.role === open) || data.roles[0];
  return (
    <div data-testid="admin-personas" className="space-y-4">
      <div data-testid="admin-personas-context" className="flex items-start gap-3 rounded-md border border-jigsaw-navy/20 bg-jigsaw-navy px-4 py-3 text-white">
        <Eye className="mt-0.5 h-4 w-4 shrink-0 text-jigsaw-orange" />
        <div className="min-w-0 text-[13px]">
          <p className="font-semibold">Read-only capability view - you are still signed in as {data.viewer.email}</p>
          <p className="text-white/80">Nothing here switches your identity, token or tenant. {data.limitation}</p>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <ul data-testid="admin-persona-list" className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:flex lg:flex-col" role="tablist" aria-label="Roles">
          {data.roles.map((r) => (
            <li key={r.role} className="min-w-0">
              <button type="button" role="tab" aria-selected={sel.role === r.role} data-testid={`persona-${r.role}`} onClick={() => setOpen(r.role)}
                className={`h-full w-full rounded-md border border-l-4 bg-white px-3 py-2 text-left transition-colors ${TONE[r.org_type]} ${sel.role === r.role ? "border-jigsaw-navy shadow-sm" : "border-jigsaw-border hover:border-jigsaw-navy/50"}`}>
                <span className="block break-words text-[13px] font-semibold text-jigsaw-navy">{r.role.replace("_", " ")}</span>
                <span className="block text-[11px] text-jigsaw-muted">{r.workspace} · {r.active_users} active</span>
              </button>
            </li>
          ))}
        </ul>
        <section data-testid="admin-persona-detail" className="rounded-lg border border-jigsaw-border bg-white p-4" aria-live="polite">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold text-jigsaw-navy">{sel.role.replace("_", " ")} <span className="text-[12px] font-normal text-jigsaw-muted">· {sel.workspace} · entry <span className="font-mono-ref">{sel.entry}</span></span></h2>
            <span className="text-[12px] text-jigsaw-muted">{sel.active_users} active account{sel.active_users === 1 ? "" : "s"}</span>
          </div>
          {sel.parity_note && <p data-testid="persona-parity-note" className="mt-2 flex items-start gap-1.5 rounded-md bg-[#FAFBFC] px-3 py-2 text-[12px] text-jigsaw-muted"><ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-jigsaw-orange" /> {sel.parity_note}</p>}
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <h3 className="text-[11px] font-semibold uppercase tracking-[.08em] text-jigsaw-muted">Navigation</h3>
              <ul className="mt-2 flex flex-wrap gap-1.5">{sel.navigation.map((n) => <li key={n} className="rounded bg-jigsaw-bg px-2 py-0.5 text-[12px] text-jigsaw-navy">{n}</li>)}</ul>
            </div>
            <div>
              <h3 className="text-[11px] font-semibold uppercase tracking-[.08em] text-jigsaw-muted">Can do</h3>
              <ul className="mt-2 list-disc space-y-1 pl-4 text-[13px] text-jigsaw-navy">{sel.capabilities.map((c) => <li key={c}>{c}</li>)}</ul>
              {sel.additional.length > 0 && (<>
                <h3 className="mt-3 text-[11px] font-semibold uppercase tracking-[.08em] text-jigsaw-orange">Admin only</h3>
                <ul data-testid="persona-admin-only" className="mt-2 list-disc space-y-1 pl-4 text-[13px] text-jigsaw-navy">{sel.additional.map((c) => <li key={c}>{c}</li>)}</ul>
              </>)}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
